import { Card, CardContent, Typography, Grid, styled, CardProps, alpha } from "@mui/material";
import classNames from "classnames";
import { IFlightDelay, IFlightATA, IAircraft, IFlightAircraft } from "@vmosolutions/oc-interfaces";
import { DisplayLastModification } from "src/components/FormikComponents/DisplayLastModification";
import { minutes2HrsMin } from "src/utils/index";
import { ATACodeChip } from "src/components/FilterSearchTable/CustomCellComponents/ATACodeChip";

export interface IEventCommentProps {
  flightDelay: IFlightDelay;
  aircraft: IAircraft | IFlightAircraft;
}

const OCFlightDelay = styled((props: CardProps) => <Card {...props} />)(({ theme }) => ({
  width: "100%",
  margin: "10px 0px",
  boxShadow: "unset",
  backgroundColor: theme.custom.comments.card.contentBackGround,
  "& .expand_button": {
    height: 28,
    marginTop: -20,
    justifyContent: "flex-end",
    padding: 0,
    "& .MuiButton-text": {
      padding: "2px 2px",
    },
  },
  "& .card_header": {
    padding: 5,
    "& .MuiCardHeader-content": {
      "& .MuiCardHeader-title": {
        fontSize: 12,
        fontWeight: 300,
        color: alpha(theme.custom.comments.card.contrastText, 0.75),
      },
      "& .MuiCardHeader-subheader": {
        fontSize: 12,
        fontWeight: 300,
        color: alpha(theme.custom.comments.card.contrastText, 0.75),
      },
    },
    "& .avatar": {
      backgroundColor: theme.custom.comments.card.avatar,
    },
  },
  "& .card_content": {
    ...theme.typography.body2,
    width: "100%",
    fontWeight: 300,
    padding: 5,
    position: "relative",
    maxHeight: 90,
    height: "auto",
    overflow: "hidden",
    whiteSpace: "pre",
    transition: theme.transitions.create("max-height", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    "&.showFade:after": {
      content: '""',
      position: "absolute",
      display: "block",
      width: "100%",
      height: 40,
      bottom: 0,
      backgroundImage: `linear-gradient(
          ${alpha(theme.custom.comments.card.contentBackGround, 0)},
          ${theme.custom.comments.card.contentBackGround})`,
      transition: theme.transitions.create("height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    "&.expanded": {
      overflowY: "auto",
      maxHeight: 1500,
      height: "auto",
      transition: theme.transitions.create("max-height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    "&.expanded.showFade:after": {
      height: 0,
      transition: theme.transitions.create("height", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  },
}));

export const FlightDelay = (props: IEventCommentProps): JSX.Element => {
  const { flightDelay, aircraft } = props; //setExpandedId, expandedId

  return (
    <OCFlightDelay className={"flight_delay_info"} style={{ height: "fit-content" }}>
      <CardContent className={classNames("expanded", "showFade", "card_content")}>
        <Grid container style={{ marginLeft: 8 }}>
          <Grid container>
            <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
              <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                Affects:
              </Typography>
              <Typography variant="caption" color="textPrimary" style={{ marginRight: 10 }}>
                {flightDelay?.onDeparture ? "Departure" : "Arrival"}
              </Typography>
            </Grid>

            <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
              <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                Duration:
              </Typography>
              <Typography variant="caption" color="textPrimary" style={{ marginRight: 10 }}>
                {minutes2HrsMin(flightDelay?.duration)}
              </Typography>
            </Grid>

            <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
              <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                Type:
              </Typography>
              <Typography variant="caption" color="textPrimary">
                {flightDelay?.delay?.title
                  .trim()
                  .concat(flightDelay?.delay?.delayCode?.delayNumber.toString())
                  .concat("-", flightDelay?.delay?.delayCode?.title)}
              </Typography>
            </Grid>

            {flightDelay?.ATA?.length > 0 && (
              <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
                <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                  ATA Code(s):
                </Typography>
                {flightDelay?.ATA?.map((ata: IFlightATA) => (
                  <Grid item key={ata?._id}>
                    <ATACodeChip flightATA={ata} showFullName={true} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          <Grid container direction="column">
            {!!aircraft?.registration && (
              <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
                <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                  Aircraft:
                </Typography>
                <Typography variant="caption" color="textPrimary">
                  {aircraft?.registration}
                </Typography>
              </Grid>
            )}

            {flightDelay?.notes?.length ? (
              <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
                <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                  Notes:
                </Typography>
                <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word", marginLeft: 10 }}>
                  {flightDelay?.notes}
                </div>
              </Grid>
            ) : null}

            {flightDelay?.otherDesc?.length ? (
              <Grid container wrap="nowrap" style={{ width: "auto", margin: 5 }}>
                <Typography variant="caption" color="inherit" style={{ marginRight: 5 }}>
                  Other Description:
                </Typography>
                <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word", marginLeft: 10 }}>
                  {flightDelay?.otherDesc}
                </div>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        <Grid item style={{ margin: "5px 0px" }}>
          <DisplayLastModification
            userId={flightDelay.modifiedBy ?? flightDelay.createdBy}
            time={flightDelay.modifiedAt ?? flightDelay.createdAt}
          />
        </Grid>
      </CardContent>
    </OCFlightDelay>
  );
};
